import { LazyQueryExecFunction } from '@apollo/client'
import { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import { ContentContainer } from '../../components/custom_components/ContentContainer'
import PerFlexRow from '../../components/custom_components/FlexRow'
import { PerInfoBox } from '../../components/custom_components/InfoBox'
import { Search, SearchType } from '../../components/custom_components/Search/Search'
import PerText from '../../components/custom_components/Text'
import { useSubordinateWithSearch } from '../../graphql/subordinates/useSubordinateWithSearch'
import { useCollapse } from '../../routes/collapse-context'
import { SubordinateSearchQuery, SubordinateSearchQueryVariables } from '../../types'
import { useEmployeeSearchStore } from '../employees/EmployeeSearch/EmployeeSearchStore'
import { useEmployeeSearchTimer } from '../employees/EmployeeSearch/useEmployeeSearchTimer'
import './SearchResultsPage.less'
import { SearchResultsTable } from './components/SearchResultsTable'
import BackgroundBox from '../../components/custom_components/BackgroundBox'
import PerButton from '../../components/custom_components/Button'

interface SearchInputProps {
  searchCallback: LazyQueryExecFunction<SubordinateSearchQuery, SubordinateSearchQueryVariables>
}

const SearchInput: FC<SearchInputProps> = ({ searchCallback }) => {
  const { t } = useTranslation()
  const search = useEmployeeSearchStore((state) => state.search)
  const [searchTerm, setSearchTerm, handleClear] = useEmployeeSearchTimer(searchCallback, search)

  const handlePageSearch = (value: string) => {
    if (!!value.length) {
      setSearchTerm(value)
    }
  }

  return (
    <Search
      type={SearchType.fixed}
      placeholder={t('navigation.search.placeholder')}
      value={searchTerm}
      onSearch={handlePageSearch}
      onChange={handleClear}
    />
  )
}

export const SearchResultsPage: FC = () => {
  const { t } = useTranslation()
  const collapsed = useCollapse()
  const [search, setSearch, searchLabel, setSearchLabel] = useEmployeeSearchStore(
    (state) => [state.search, state.setSearch, state.searchLabel, state.setSearchLabel],
    shallow,
  )
  const { loadSubordinates, subordinates, loading, hasMore, loadMore } = useSubordinateWithSearch()

  useEffect(() => {
    if (search.length > 2) {
      loadSubordinates({ variables: { search, pagination: {} } })
    }
    return () => {
      setSearch('')
      setSearchLabel('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ContentContainer collapsed={collapsed}>
      <PerInfoBox>
        <PerFlexRow width="fullWidth" direction="column" className="container">
          <PerText appearance="tile-heading" mb="l">
            {!!searchLabel.length
              ? t('searchResults.numberOfResults', { count: subordinates.length, value: searchLabel })
              : t('searchResults.noSearchTerm')}
          </PerText>
          <SearchInput searchCallback={loadSubordinates} />
        </PerFlexRow>
      </PerInfoBox>

      <SearchResultsTable data={subordinates} loading={loading} showMoreButtonVisible={hasMore && subordinates.length > 0} />

      {!loading && hasMore && subordinates.length > 0 && (
        <BackgroundBox mt="xxxs" r height="tabbar" noPadding>
          <PerFlexRow align="middle" justify="center" className="fullHeight">
            <PerButton color="white" text="Show more" onClick={loadMore} />
          </PerFlexRow>
        </BackgroundBox>
      )}
    </ContentContainer>
  )
}
